import API_BASE_URL from '../config/apiConfig';
import { apiClient, getAuthToken } from './authService';
import { isBnbUnit } from '../../utils/publicListingScope';
import { isUnitNotFoundError } from '../../utils/publicUnitValidation';
import { parseUnitsFromApiResponse } from '../../utils/propertyUnitStats';

/**
 * Upload property image
 * @param {File} file - Image file to upload
 * @param {string} propertyId - Property ID (optional, for existing properties)
 * @returns {Promise<Object>} Upload response with status and data containing URL
 */
export const uploadPropertyImage = async (file, propertyId = null) => {
  try {
    const token = getAuthToken();

    if (!token) {
      const error = new Error('Authentication token not found. Please log in again.');
      error.status = 401;
      throw error;
    }

    const formData = new FormData();
    formData.append('file', file);
    if (propertyId) {
      formData.append('propertyId', propertyId);
    }
    formData.append('category', 'property');

    const response = await apiClient(
      `${API_BASE_URL}/api/files/upload`,
      {
        method: 'POST',
        body: formData,
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'File upload failed';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Upload property image error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Upload agreement document
 * @param {File} file - Agreement file to upload
 * @param {string} propertyId - Property ID (optional, for existing properties)
 * @returns {Promise<Object>} Upload response with status and data containing URL
 */
export const uploadAgreementDocument = async (file, propertyId = null) => {
  try {
    const token = getAuthToken();

    if (!token) {
      const error = new Error('Authentication token not found. Please log in again.');
      error.status = 401;
      throw error;
    }

    const formData = new FormData();
    formData.append('file', file);
    if (propertyId) {
      formData.append('propertyId', propertyId);
    }
    formData.append('category', 'agreement');

    const response = await apiClient(
      `${API_BASE_URL}/api/files/upload`,
      {
        method: 'POST',
        body: formData,
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'File upload failed';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Upload agreement document error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Create a new property
 * @param {Object} propertyData - Property data
 * @param {string} propertyData.name - Property name
 * @param {string} propertyData.address - Property address
 * @param {Object} propertyData.location - Location object with lat, lng, city (and optional area/locality/suburb)
 * @param {string} propertyData.description - Property description
 * @param {Array<string>} propertyData.amenities - Array of amenities
 * @param {Array<string>} propertyData.imageUrls - Array of image URLs (min 1, max 6)
 * @param {Object} propertyData.contacts - Contacts object with phone, email, caretakerPhone
 * @param {Object} propertyData.paymentDetails - Payment details object
 * @param {string} propertyData.agreementUrl - Agreement document URL (optional)
 * @param {number} propertyData.unitCount - Number of units
 * @param {boolean} propertyData.published - Whether property is publicly visible (optional)
 * @param {number} propertyData.rentGraceDays - Rent grace period in days (optional)
 * @param {string} propertyData.lateFeeType - FIXED_PER_DAY | PERCENT_OF_RENT_PER_DAY (optional)
 * @param {number} propertyData.lateFeeValue - Late fee value (optional)
 * @param {boolean} propertyData.waterIncludedInRent - Whether water is included in rent (optional)
 * @param {number} propertyData.waterRatePerUnit - Water charge per unit when not included in rent (optional)
 * @returns {Promise<Object>} Create property response
 */
export const createProperty = async (propertyData) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/properties`,
      {
        method: 'POST',
        body: JSON.stringify(propertyData),
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to create property';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Create property error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Create a property on behalf of a landlord (agent flow).
 * Endpoint: POST /api/properties?landlordId={landlordId}
 * @param {string} landlordId - Landlord ID
 * @param {Object} propertyData - Property payload
 * @returns {Promise<Object>} Create property response
 */
export const createPropertyForLandlord = async (landlordId, propertyData) => {
  try {
    const landlordIdValue = String(landlordId || '').trim();
    if (!landlordIdValue) {
      throw new Error('Landlord is required');
    }

    const queryParams = new URLSearchParams({ landlordId: landlordIdValue });
    const response = await apiClient(
      `${API_BASE_URL}/api/properties?${queryParams.toString()}`,
      {
        method: 'POST',
        body: JSON.stringify(propertyData),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to create property for landlord';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Create property for landlord error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get properties for the current user
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 0)
 * @param {number} params.size - Page size (default: 20)
 * @param {string} params.search - Search query (optional)
 * @returns {Promise<Object>} Properties response with pagination
 */
export const getMyProperties = async ({ page = 0, size = 20, search = '', dashboardScope } = {}) => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (search && search.trim()) {
      queryParams.append('search', search.trim());
    }

    // Optional dashboard scope (e.g., 'BNB')
    if (dashboardScope && String(dashboardScope).trim() !== '') {
      queryParams.append('dashboardScope', String(dashboardScope).trim());
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/properties/my-properties?${queryParams.toString()}`,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch properties';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get properties error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * System admin: list all properties (paginated)
 * @param {number} page - Page index (0-based)
 * @param {number} size - Page size
 * @returns {Promise<Object>} API wrapper with data: Spring Page of properties
 */
export const getAdminAllProperties = async (page = 0, size = 20, search = '') => {
  try {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });
    if (search && search.trim()) {
      params.append('search', search.trim());
    }
    const response = await apiClient(
      `${API_BASE_URL}/api/properties/admin/all?${params.toString()}`,
      {
        method: 'GET',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch properties';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get admin properties error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get property by ID with optional units
 * @param {string} propertyId - Property ID
 * @param {boolean} includeUnits - Whether to include units (default: true)
 * @returns {Promise<Object>} Property response with full details
 */
export const getPropertyById = async (propertyId, includeUnits = true, options = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (includeUnits) {
      queryParams.append('includeUnits', 'true');
    }
    const scope = String(options?.dashboardScope || '').trim();
    if (scope) {
      queryParams.append('dashboardScope', scope);
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/properties/${propertyId}?${queryParams.toString()}`,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch property';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get property by ID error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * List rent evidence submissions for a property (landlord / agent).
 * GET /api/properties/{propertyId}/rent-evidence?year=&month=&status=&unitId=
 * @param {string} propertyId
 * @param {{ year?: number, month?: number, status?: string, unitId?: string }} [query]
 */
export const getRentEvidence = async (propertyId, query = {}) => {
  try {
    const params = new URLSearchParams();
    if (query.year != null && query.year !== '') {
      params.append('year', String(query.year));
    }
    if (query.month != null && query.month !== '') {
      params.append('month', String(query.month));
    }
    if (query.status != null && String(query.status).trim() !== '') {
      params.append('status', String(query.status).trim());
    }
    if (query.unitId != null && String(query.unitId).trim() !== '') {
      params.append('unitId', String(query.unitId).trim());
    }

    const qs = params.toString();
    const url = `${API_BASE_URL}/api/properties/${propertyId}/rent-evidence${qs ? `?${qs}` : ''}`;

    const response = await apiClient(url, { method: 'GET' }, true);

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to load rent evidence';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get rent evidence error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

function isApiEnvelopeSuccess(data) {
  const s = data?.status;
  return s === 'success' || s === 'SUCCESS';
}

/**
 * Approve a rent evidence submission.
 * POST /api/properties/{propertyId}/rent-evidence/{evidenceId}/approve
 */
export const approveRentEvidence = async (propertyId, evidenceId) => {
  try {
    const url = `${API_BASE_URL}/api/properties/${propertyId}/rent-evidence/${evidenceId}/approve`;
    const response = await apiClient(url, { method: 'POST', body: JSON.stringify({}) }, true);
    const data = await response.json().catch(() => ({}));
    if (!response.ok && !isApiEnvelopeSuccess(data)) {
      const errorMessage = data.message || data.error || 'Failed to approve rent evidence';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }
    return data;
  } catch (error) {
    console.error('Approve rent evidence error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Reject a rent evidence submission.
 * POST /api/properties/{propertyId}/rent-evidence/{evidenceId}/reject
 * @param {{ rejectionReason?: string, reason?: string }} [body]
 */
export const rejectRentEvidence = async (propertyId, evidenceId, body = {}) => {
  try {
    const url = `${API_BASE_URL}/api/properties/${propertyId}/rent-evidence/${evidenceId}/reject`;
    const raw =
      body.rejectionReason ??
      body.reason ??
      '';
    const trimmed = String(raw).trim();
    const payload = {};
    if (trimmed !== '') {
      payload.rejectionReason = trimmed;
      // Some backends bind `reason` only; send both when we have text
      payload.reason = trimmed;
    }
    const response = await apiClient(url, { method: 'POST', body: JSON.stringify(payload) }, true);
    const data = await response.json().catch(() => ({}));
    if (!response.ok && !isApiEnvelopeSuccess(data)) {
      const errorMessage = data.message || data.error || 'Failed to reject rent evidence';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }
    return data;
  } catch (error) {
    console.error('Reject rent evidence error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/** @returns {Array} rent-evidence rows from a list API response */
export const parseRentEvidenceResponse = (res) => {
  const raw = res?.data;
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.content)) return raw.content;
  return [];
};

const UUID_IN_STRING_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const EVIDENCE_PATH_KEY_RE = /evidence|deposit|first.?month|first_month|payment.*proof/i;

function normalizeMpesaText(text) {
  return String(text || '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function mpesaTextsMatch(rowText, reqText) {
  const a = normalizeMpesaText(rowText);
  const b = normalizeMpesaText(reqText);
  if (!a || !b) return false;
  if (a === b) return true;
  const ref = b.match(/\b([a-z0-9]{8,12})\b/i);
  if (ref && a.includes(String(ref[1]).toLowerCase())) return true;
  const sliceLen = Math.min(48, a.length, b.length);
  if (sliceLen >= 20 && a.slice(0, sliceLen) === b.slice(0, sliceLen)) return true;
  return a.includes(b.slice(0, 36)) || b.includes(a.slice(0, 36));
}

function getMpesaMessageFromRentEvidenceRow(row) {
  return String(
    row?.mpesaMessage ??
      row?.mpesa_message ??
      row?.message ??
      row?.evidenceText ??
      row?.evidence_text ??
      row?.paymentMessage ??
      row?.payment_message ??
      row?.description ??
      row?.notes ??
      row?.text ??
      ''
  );
}

function amountMatchesRentEvidenceRow(row, amount) {
  if (amount == null || amount === '') return false;
  const target = Number(amount);
  if (!Number.isFinite(target)) return false;
  const paid = Number(row?.amountPaid ?? row?.amount ?? row?.amount_paid);
  return Number.isFinite(paid) && Math.abs(paid - target) < 0.01;
}

function buildRejectReasonBody(reason) {
  const body = {};
  if (reason != null && String(reason).trim() !== '') {
    const trimmed = String(reason).trim();
    body.reason = trimmed;
    body.rejectionReason = trimmed;
    body.rejection_reason = trimmed;
  }
  return body;
}

/**
 * Rent-evidence record ids for deposit / first-month proof on a unit request.
 * @param {Object} req
 * @returns {string[]}
 */
export const getRentEvidenceIdsFromUnitRequest = (req) => {
  const ids = [];
  const requestIdLower = String(req?.id || '').toLowerCase();
  const add = (id) => {
    const s = String(id ?? '').trim();
    if (!s || s.toLowerCase() === requestIdLower) return;
    if (!ids.some((x) => String(x).toLowerCase() === s.toLowerCase())) ids.push(s);
  };

  add(req?.depositEvidenceId);
  add(req?.firstMonthEvidenceId);
  add(req?.depositRentEvidenceId);
  add(req?.firstMonthRentEvidenceId);
  add(req?.deposit_evidence_id);
  add(req?.first_month_evidence_id);
  add(req?.depositPaymentEvidenceId);
  add(req?.firstMonthPaymentEvidenceId);
  add(req?.depositRentEvidenceRecordId);
  add(req?.firstMonthRentEvidenceRecordId);

  const dep = req?.depositEvidence;
  const fm = req?.firstMonthEvidence;
  if (dep && typeof dep === 'object') add(dep.id ?? dep.evidenceId ?? dep.rentEvidenceId);
  if (fm && typeof fm === 'object') add(fm.id ?? fm.evidenceId ?? fm.rentEvidenceId);

  for (const url of [req?.depositEvidenceUrl, req?.firstMonthEvidenceUrl]) {
    if (!url) continue;
    const m = String(url).match(UUID_IN_STRING_RE);
    if (m) add(m[0]);
  }

  const walk = (obj, depth = 0, parentKey = '') => {
    if (!obj || depth > 6) return;
    if (Array.isArray(obj)) {
      obj.forEach((item) => walk(item, depth + 1, parentKey));
      return;
    }
    if (typeof obj !== 'object') return;
    for (const [key, val] of Object.entries(obj)) {
      const path = `${parentKey}.${key}`;
      if (val != null && typeof val === 'object' && !Array.isArray(val)) {
        if (EVIDENCE_PATH_KEY_RE.test(key)) {
          add(val.id ?? val.evidenceId ?? val.rentEvidenceId);
        }
        walk(val, depth + 1, path);
      } else if (typeof val === 'string') {
        if (/evidence.*id$/i.test(key) || /rentEvidenceId$/i.test(key)) {
          add(val);
        } else if (EVIDENCE_PATH_KEY_RE.test(path) && UUID_IN_STRING_RE.test(val)) {
          const m = val.match(UUID_IN_STRING_RE);
          if (m) add(m[0]);
        }
      }
    }
  };
  walk(req);

  return ids;
};

/** True when POST .../unit-requests/{id}/reject is valid (REQUESTED only). */
export const isUnitRequestPendingForReject = (req) =>
  String(req?.status || '').toUpperCase() === 'REQUESTED';

function rentEvidenceRowMatchesUnitRequest(row, req) {
  const requestId = String(req?.id || '').toLowerCase();
  const rowReqId = String(
    row?.unitRequestId ??
      row?.unit_request_id ??
      row?.requestId ??
      row?.bookingRequestId ??
      ''
  ).toLowerCase();
  if (requestId && rowReqId && rowReqId === requestId) return true;

  const photo = String(row?.photoUrl ?? row?.photo_url ?? '');
  if (req?.depositEvidenceUrl && photo && photo === req.depositEvidenceUrl) return true;
  if (req?.firstMonthEvidenceUrl && photo && photo === req.firstMonthEvidenceUrl) return true;

  const rowMpesa = getMpesaMessageFromRentEvidenceRow(row);
  if (req?.depositEvidenceText && mpesaTextsMatch(rowMpesa, req.depositEvidenceText)) return true;
  if (req?.firstMonthEvidenceText && mpesaTextsMatch(rowMpesa, req.firstMonthEvidenceText)) return true;

  const rowDepositText = String(row?.depositEvidenceText ?? row?.deposit_evidence_text ?? '');
  const rowFirstText = String(row?.firstMonthEvidenceText ?? row?.first_month_evidence_text ?? '');
  if (req?.depositEvidenceText && rowDepositText && mpesaTextsMatch(rowDepositText, req.depositEvidenceText)) {
    return true;
  }
  if (req?.firstMonthEvidenceText && rowFirstText && mpesaTextsMatch(rowFirstText, req.firstMonthEvidenceText)) {
    return true;
  }

  const unitId = String(
    req?.unitId ?? req?.unitID ?? req?.propertyUnitId ?? req?.propertyUnitID ?? ''
  ).toLowerCase();
  const rowUnit = String(row?.unitId ?? row?.unit_id ?? row?.propertyUnitId ?? '').toLowerCase();
  if (unitId && rowUnit && unitId !== rowUnit) return false;

  const depositAmt = req?.depositPaidAmount ?? req?.depositAmount;
  const firstAmt = req?.firstMonthPaidAmount ?? req?.monthlyRent;
  if (depositAmt != null && amountMatchesRentEvidenceRow(row, depositAmt)) return true;
  if (firstAmt != null && amountMatchesRentEvidenceRow(row, firstAmt)) return true;

  const tenantId = String(
    req?.requesterUserId ?? req?.tenantUserId ?? req?.userId ?? ''
  ).toLowerCase();
  const rowTenant = String(
    row?.tenantUserId ?? row?.tenant_user_id ?? row?.userId ?? ''
  ).toLowerCase();
  const rowStatus = String(row?.status || '').toUpperCase();
  if (
    tenantId &&
    rowTenant &&
    tenantId === rowTenant &&
    (rowStatus === 'PENDING' || rowStatus === 'SUBMITTED' || rowStatus === '') &&
    (req?.depositEvidenceText || req?.firstMonthEvidenceText)
  ) {
    return true;
  }

  return false;
}

async function resolveRentEvidenceIdsFromApi(propertyId, req) {
  const unitId =
    req?.unitId || req?.unitID || req?.unit?.id || req?.propertyUnitId || req?.propertyUnitID || null;
  const queryVariants = [
    { unitId: unitId || undefined, status: 'PENDING' },
    { unitId: unitId || undefined, status: 'SUBMITTED' },
    { unitId: unitId || undefined },
    { status: 'PENDING' },
    {},
  ];

  const seen = new Set();
  const matched = [];

  for (const query of queryVariants) {
    try {
      const res = await getRentEvidence(propertyId, query);
      const list = parseRentEvidenceResponse(res);
      for (const row of list) {
        const id = row?.id;
        if (!id || seen.has(id)) continue;
        if (!rentEvidenceRowMatchesUnitRequest(row, req)) continue;
        seen.add(id);
        matched.push(id);
      }
      if (matched.length >= 2) break;
    } catch {
      // try next query shape
    }
  }

  return matched;
}

/**
 * Reject deposit / first-month payment evidence (not the whole unit request).
 * Tries rent-evidence reject per record, then unit-request reject-payments.
 * @param {string} propertyId
 * @param {Object} req - Unit request row with evidence fields
 * @param {{ reason?: string, properties?: Array }} [options]
 */
export const rejectUnitRequestPaymentEvidence = async (propertyId, req, { reason, properties } = {}) => {
  let merged = { ...req, propertyId: req?.propertyId || propertyId };

  if (merged?.id && propertyId) {
    try {
      const detailRes = await getPropertyUnitRequestById(propertyId, merged.id);
      const detail = detailRes?.data ?? detailRes;
      if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
        merged = { ...merged, ...detail, propertyId };
      }
    } catch {
      // detail endpoint optional; fall back to list row
    }
  }

  if (Array.isArray(properties) && properties.length > 0 && merged?.id) {
    try {
      const fresh = await findPropertyUnitRequestById(properties, merged.id);
      if (fresh) merged = { ...merged, ...fresh, propertyId: fresh.propertyId || propertyId };
    } catch {
      // use in-memory row
    }
  }

  let ids = getRentEvidenceIdsFromUnitRequest(merged);
  if (ids.length === 0) {
    ids = await resolveRentEvidenceIdsFromApi(propertyId, merged);
  }

  if (ids.length > 0) {
    let lastRes;
    for (const evidenceId of ids) {
      lastRes = await rejectRentEvidence(propertyId, evidenceId, { reason });
    }
    return lastRes;
  }

  return rejectUnitRequestPayments(propertyId, merged.id, { reason });
};

/**
 * Get public property by ID (no auth required) with optional units
 * Endpoint: GET /api/public/properties/{propertyId}
 * @param {string} propertyId - Property ID
 * @param {boolean} includeUnits - Whether to include units (default: true)
 * @param {{ dashboardScope?: 'BNB' | 'RENTAL' }} [options]
 * @returns {Promise<Object>} Public property response with full details
 */
export const getPublicPropertyById = async (propertyId, includeUnits = true, options = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (includeUnits) {
      queryParams.append('includeUnits', 'true');
    }
    const scope = String(options?.dashboardScope || '').trim();
    if (scope) {
      queryParams.append('dashboardScope', scope);
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/public/properties/${propertyId}?${queryParams.toString()}`,
      {
        method: 'GET',
      },
      false // Public endpoint – no authentication required
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch public property';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get public property by ID error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

const PUBLIC_RENTAL_UNIT_VERIFY_URLS = (unitId) => [
  `${API_BASE_URL}/api/public/rental/units/${unitId}`,
  `${API_BASE_URL}/api/public/properties/units/${unitId}`,
  `${API_BASE_URL}/api/public/properties/unit/${unitId}`,
  `${API_BASE_URL}/api/public/units/${unitId}`,
];

async function verifyPublicRentalUnitExists(unitId) {
  const id = String(unitId || '').trim();
  if (!id) return false;

  let confirmedMissing = false;
  for (const url of PUBLIC_RENTAL_UNIT_VERIFY_URLS(id)) {
    try {
      const response = await apiClient(url, { method: 'GET' }, false);
      if (response.ok) return true;
      if (response.status === 404 || response.status === 400) {
        confirmedMissing = true;
      }
    } catch (_) {
      // try next pattern
    }
  }
  if (confirmedMissing) return false;
  return true;
}

async function verifyPublicUnitStillListed(unit, scope) {
  const unitId = String(unit?.id ?? unit?.unitId ?? '').trim();
  if (!unitId) return false;

  const isBnb = scope === 'BNB' || isBnbUnit(unit);
  if (isBnb) {
    try {
      await getPublicBnbUnitById(unitId);
      return true;
    } catch (error) {
      if (isUnitNotFoundError(error)) return false;
      return true;
    }
  }

  return verifyPublicRentalUnitExists(unitId);
}

/**
 * Drop units that were deleted on the landlord side but still appear in GET /api/public/properties.
 * Uses the live units API when the viewer is the owner/agent; otherwise probes public unit endpoints.
 *
 * @param {string} propertyId
 * @param {Array<object>} units
 * @param {'BNB' | 'RENTAL'} [scope]
 * @returns {Promise<Array<object>>}
 */
export async function reconcilePublicPropertyUnits(propertyId, units, scope = 'RENTAL') {
  if (!Array.isArray(units) || units.length === 0) return [];

  const scopeNorm = String(scope || 'RENTAL').toUpperCase() === 'BNB' ? 'BNB' : 'RENTAL';
  const token = getAuthToken();

  if (token && propertyId) {
    try {
      const unitsResponse = await getPropertyUnits(propertyId, {
        dashboardScope: scopeNorm,
      });
      const liveUnits = parseUnitsFromApiResponse(unitsResponse);
      const liveIds = new Set(
        liveUnits
          .map((u) => String(u?.id ?? u?.unitId ?? '').trim())
          .filter(Boolean)
      );
      return units.filter((u) => liveIds.has(String(u?.id ?? u?.unitId ?? '').trim()));
    } catch (_) {
      // Viewer is not owner/agent — fall through to public probes
    }
  }

  const verified = await Promise.all(
    units.map(async (unit) => ((await verifyPublicUnitStillListed(unit, scopeNorm)) ? unit : null))
  );
  return verified.filter(Boolean);
}

/**
 * Request a unit as a tenant (authenticated)
 * Endpoint: POST /api/properties/{propertyId}/units/{unitId}/requests
 * @param {string} propertyId - Property ID
 * @param {string} unitId - Unit ID
 * @param {Object} options - Request options
 * @param {string} [options.notes] - Optional notes (e.g. move-in date)
 * @returns {Promise<Object>} Response with status, message, and data (request details)
 */
export const requestUnit = async (propertyId, unitId, { notes } = {}) => {
  try {
    const url = `${API_BASE_URL}/api/properties/${propertyId}/units/${unitId}/requests`;
    const body = {};
    if (notes != null && String(notes).trim() !== '') {
      body.notes = String(notes).trim();
    }

    const response = await apiClient(
      url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to request unit';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Request unit error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get current user's unit requests (authenticated)
 * Endpoint: GET /api/unit-requests/my
 * @returns {Promise<Object>} Response with status, message, and data (array of request objects)
 */
export const getMyUnitRequests = async () => {
  try {
    const url = `${API_BASE_URL}/api/unit-requests/my`;
    const response = await apiClient(url, { method: 'GET' }, true);
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch unit requests';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get my unit requests error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get current user's BnB bookings (authenticated)
 * Endpoint: GET /api/bnb/bookings/my
 * @returns {Promise<Object>} Response with status, message, and data (array of booking objects)
 */
export const getMyBnbBookings = async () => {
  try {
    const url = `${API_BASE_URL}/api/bnb/bookings/my`;
    const response = await apiClient(url, { method: 'GET' }, true);
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch BnB bookings';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get my BnB bookings error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Search tenant users by identifier (phone, ID number, or name).
 * Endpoint: GET /api/tenants/search?identifier={value}
 * @param {string} identifier
 * @returns {Promise<Object>} Response with data: [{ tenantUserId, tenantCode, firstName, lastName }]
 */
export const searchTenantsByIdentifier = async (identifier) => {
  const value = String(identifier || '').trim();
  if (!value) return { status: 'success', data: [] };

  try {
    const queryParams = new URLSearchParams({ identifier: value });
    const url = `${API_BASE_URL}/api/tenants/search?${queryParams.toString()}`;
    const response = await apiClient(url, { method: 'GET' }, true);
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to search tenants';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Search tenants error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Manage BnB bookings for a specific property (authenticated)
 * Endpoint: GET /api/bnb/bookings/manage?propertyId={{propertyId}}&status={{status}}
 * @param {string} propertyId
 * @param {string} [status]
 * @returns {Promise<Object>} Response with status, message, and data (array of booking objects)
 */
export const getManageBnbBookings = async (propertyId, status) => {
  try {
    let url = `${API_BASE_URL}/api/bnb/bookings/manage?propertyId=${encodeURIComponent(propertyId)}`;
    if (status && String(status).trim() !== '' && String(status).toLowerCase() !== 'all') {
      url += `&status=${encodeURIComponent(String(status).trim())}`;
    }

    const response = await apiClient(url, { method: 'GET' }, true);
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch manage BnB bookings';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get manage BnB bookings error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/** @returns {Array} booking rows from a manage-BnB-bookings API response */
export const parseManageBnbBookingsResponse = (res) => {
  const d = res?.data;
  return Array.isArray(d) ? d : [];
};

/**
 * Find one BnB booking by id across properties (any status — confirmed, rejected, etc.).
 * @param {Array<{ id: string }>|string[]} properties
 * @param {string|number} bookingId
 */
export const findManageBnbBookingById = async (properties, bookingId) => {
  const id = String(bookingId ?? '').trim();
  if (!id || !Array.isArray(properties) || properties.length === 0) return null;

  for (const property of properties) {
    const propertyId = property?.id ?? property;
    if (!propertyId) continue;
    try {
      const res = await getManageBnbBookings(propertyId);
      const list = parseManageBnbBookingsResponse(res);
      const found = list.find((b) => String(b.id).toLowerCase() === id.toLowerCase());
      if (found) {
        return {
          ...found,
          propertyId: found.propertyId || propertyId,
        };
      }
    } catch {
      // try next property
    }
  }
  return null;
};

/** @returns {Array} unit-request rows from a property unit-requests API response */
export const parsePropertyUnitRequestsResponse = (res) => {
  const d = res?.data;
  return Array.isArray(d) ? d : [];
};

/**
 * Find one unit request by id across properties (any status).
 * @param {Array<{ id: string }>|string[]} properties
 * @param {string|number} requestId
 */
export const findPropertyUnitRequestById = async (properties, requestId) => {
  const id = String(requestId ?? '').trim();
  if (!id || !Array.isArray(properties) || properties.length === 0) return null;

  for (const property of properties) {
    const propertyId = property?.id ?? property;
    if (!propertyId) continue;
    try {
      const res = await getPropertyUnitRequests(propertyId, { status: 'all' });
      const list = parsePropertyUnitRequestsResponse(res);
      const found = list.find((r) => String(r.id).toLowerCase() === id.toLowerCase());
      if (found) {
        return {
          ...found,
          propertyId: found.propertyId || propertyId,
        };
      }
    } catch {
      // try next property
    }
  }
  return null;
};

/**
 * Recored a booking fee payment for a BnB booking.
 * Endpoint: POST /api/payments
 * @param {string} bookingId
 * @param {number} [amount] - Confirmed amount
 * @returns {Promise<Object>} Response
 */
export const recordBnbBookingPayment = async (bookingId, amount) => {
  try {
    const url = `${API_BASE_URL}/api/payments`;
    const payload = {
      bookingId,
      type: 'BOOKING_FEE',
      amount: Number(amount || 0)
    };
    const response = await apiClient(url, { method: 'POST', body: JSON.stringify(payload) }, true);
    const data = await response.json().catch(() => ({}));

    if (!response.ok && !isApiEnvelopeSuccess(data)) {
      const errorMessage = data.message || data.error || 'Failed to record booking payment';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Record BnB booking payment error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Confirm a pending BnB booking.
 * Endpoint: POST /api/bnb/bookings/{bookingId}/confirm
 * @param {string} bookingId
 * @param {number} [amount] - Confirmed amount (often totalAmount)
 * @returns {Promise<Object>} Response
 */
export const confirmBnbBooking = async (bookingId, amount) => {
  try {
    const url = `${API_BASE_URL}/api/bnb/bookings/${encodeURIComponent(bookingId)}/confirm`;
    const payload = {};
    if (amount != null) payload.amount = Number(amount);

    const response = await apiClient(url, { method: 'POST', body: JSON.stringify(payload) }, true);
    const data = await response.json().catch(() => ({}));

    if (!response.ok && !isApiEnvelopeSuccess(data)) {
      const errorMessage = data.message || data.error || 'Failed to confirm BnB booking';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Confirm BnB booking error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};



/**
 * List unit requests for a property (landlord / agent on assigned property)
 * GET /api/properties/{propertyId}/unit-requests?status=
 * @param {string} propertyId
 * @param {{ status?: string }} [options] - status filter; omit or 'all' uses empty ?status=
 */
/**
 * Get one unit request for a property (landlord / agent).
 * GET /api/properties/{propertyId}/unit-requests/{requestId}
 */
export const getPropertyUnitRequestById = async (propertyId, requestId) => {
  try {
    const url = `${API_BASE_URL}/api/properties/${propertyId}/unit-requests/${encodeURIComponent(String(requestId).trim())}`;
    const response = await apiClient(url, { method: 'GET' }, true);
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch unit request';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get property unit request by ID error:', error);
    if (!error.response) {
      const networkError = new Error(
        error.message || 'Network error. Please check your connection and try again.'
      );
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

export const getPropertyUnitRequests = async (propertyId, { status } = {}) => {
  try {
    let url = `${API_BASE_URL}/api/properties/${propertyId}/unit-requests`;
    if (status && String(status).trim() !== '' && String(status).toLowerCase() !== 'all') {
      url += `?status=${encodeURIComponent(String(status).trim())}`;
    }

    const response = await apiClient(url, { method: 'GET' }, true);
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch property unit requests';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get property unit requests error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Request access to a tenant's score (landlord / agent).
 * Endpoint: POST /api/tenants/{tenantUserId}/score-access-requests
 * @param {string} tenantUserId
 * @returns {Promise<Object>} Response with status, message, and data (created request)
 */
export const requestTenantScoreAccess = async (tenantUserId) => {
  const resolvedTenantUserId = String(tenantUserId || '').trim();
  if (!resolvedTenantUserId) {
    throw new Error('Tenant user is required to request tenant score access.');
  }

  try {
    const url = `${API_BASE_URL}/api/tenants/${encodeURIComponent(resolvedTenantUserId)}/score-access-requests`;
    const response = await apiClient(
      url,
      {
        method: 'POST',
      },
      true
    );
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to request tenant score access';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Request tenant score access error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Outgoing score access requests for the current landlord (server-side list).
 *
 * Creating a request is POST /api/tenants/{tenantUserId}/score-access-requests (see requestTenantScoreAccess).
 * A separate “list all my outgoing requests” URL is not part of the documented API and
 * /api/landlords/score-access-requests currently errors on the server; this helper returns an empty list
 * so the UI does not call a broken route. Status is still tracked via localStorage and create / score-fetch flows.
 *
 * When the backend exposes a supported list endpoint, implement the HTTP call here.
 * @returns {Promise<{ data: Array }>}
 */
export const getLandlordOutgoingScoreAccessRequests = async () => ({ data: [] });

/**
 * Outgoing score access requests for the current agent (server-side list).
 *
 * Creating a request uses requestTenantScoreAccessAsAgent (POST under /api/agent/tenants/... or shared tenants route).
 * GET /api/agent/score-access-requests is not a working documented list API; return an empty list to avoid
 * errors until the backend exposes a supported route. Status sync uses localStorage and create / score-fetch flows.
 *
 * When the backend provides a list endpoint, implement the HTTP call here.
 * @returns {Promise<{ data: Array }>}
 */
export const getAgentOutgoingScoreAccessRequests = async () => ({ data: [] });

/**
 * View a specific tenant's score (landlord / agent).
 * Endpoint: GET /api/tenants/{tenantUserId}/score?monthsBack=
 * @param {string} tenantUserId
 * @param {number} [monthsBack=12]
 * @returns {Promise<Object>} Response with score payload
 */
export const getTenantScoreByUserId = async (tenantUserId, monthsBack = 12) => {
  const resolvedTenantUserId = String(tenantUserId || '').trim();
  const resolvedMonthsBack = Number(monthsBack);
  if (!resolvedTenantUserId) {
    throw new Error('Tenant user is required to view tenant score.');
  }

  const params = new URLSearchParams();
  params.set('monthsBack', String(Number.isFinite(resolvedMonthsBack) && resolvedMonthsBack > 0 ? resolvedMonthsBack : 12));

  try {
    const url = `${API_BASE_URL}/api/tenants/${encodeURIComponent(resolvedTenantUserId)}/score?${params.toString()}`;
    const response = await apiClient(
      url,
      {
        method: 'GET',
      },
      true
    );
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch tenant score';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    // Some backends wrap responses with an envelope { status, message, data }.
    // Treat non-success envelopes as errors rather than silently granting access.
    const envelopeSaysSuccess = isApiEnvelopeSuccess(data);
    const looksLikeScorePayload =
      data && typeof data === 'object' && (
        Object.prototype.hasOwnProperty.call(data, 'tenantScore') ||
        Object.prototype.hasOwnProperty.call(data, 'totalMonthsCounted') ||
        Object.prototype.hasOwnProperty.call(data, 'onTimeCount') ||
        Object.prototype.hasOwnProperty.call(data, 'lateCount') ||
        Object.prototype.hasOwnProperty.call(data, 'missedCount')
      );

    if (!envelopeSaysSuccess && !looksLikeScorePayload) {
      const msg = data?.message || data?.error || 'Tenant score not available (awaiting approval?)';
      const error = new Error(msg);
      error.response = data;
      error.status = response.status || 403;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get tenant score by user ID error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Request access to a tenant's score as an assigned agent (agent JWT).
 * Uses the same endpoint as landlord flow.
 * Endpoint: POST /api/tenants/{tenantUserId}/score-access-requests
 * @param {string} tenantUserId
 * @param {{ propertyId?: string }} [options]
 */
export const requestTenantScoreAccessAsAgent = async (tenantUserId, options = {}) => {
  const resolvedTenantUserId = String(tenantUserId || '').trim();
  if (!resolvedTenantUserId) {
    throw new Error('Tenant user is required to request tenant score access.');
  }
  void options; // kept for compatibility with current call sites
  return requestTenantScoreAccess(resolvedTenantUserId);
};

/**
 * View a specific tenant's score as an assigned agent (agent JWT).
 * Uses the same endpoint as landlord flow.
 * Endpoint: GET /api/tenants/{tenantUserId}/score?monthsBack=
 * @param {string} tenantUserId
 * @param {number} [monthsBack=12]
 * @param {{ propertyId?: string }} [options]
 */
export const getTenantScoreByUserIdAsAgent = async (tenantUserId, monthsBack = 12, options = {}) => {
  const resolvedTenantUserId = String(tenantUserId || '').trim();
  if (!resolvedTenantUserId) {
    throw new Error('Tenant user is required to view tenant score.');
  }
  void options; // kept for compatibility with current call sites
  return getTenantScoreByUserId(resolvedTenantUserId, monthsBack);
};

/**
 * View a specific tenant's score using their unique tenant code.
 * Endpoint: GET /api/tenants/score-by-code?code={code}&monthsBack={months}
 * @param {string} tenantCode
 * @param {number} [monthsBack=12]
 */
export const getTenantScoreByCode = async (tenantCode, monthsBack = 12) => {
  const resolvedCode = String(tenantCode || '').trim();
  if (!resolvedCode) {
    throw new Error('Tenant code is required to view tenant score.');
  }

  const params = new URLSearchParams();
  params.set('code', resolvedCode);
  params.set('monthsBack', String(monthsBack));

  try {
    const url = `${API_BASE_URL}/api/tenants/score-by-code?${params.toString()}`;
    const response = await apiClient(
      url,
      {
        method: 'GET',
      },
      true
    );
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch tenant score by code';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get tenant score by code error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Approve a unit request (landlord / agent on assigned property)
 * Endpoint: POST /api/properties/{propertyId}/unit-requests/{requestId}/approve
 * @param {string} propertyId
 * @param {string} requestId
 * @returns {Promise<Object>} Response with status, message, and data (updated request)
 */
export const approveUnitRequest = async (propertyId, requestId) => {
  try {
    const url = `${API_BASE_URL}/api/properties/${propertyId}/unit-requests/${requestId}/approve`;
    const response = await apiClient(
      url,
      {
        method: 'POST',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to approve unit request';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Approve unit request error:', error);
    if (!error.response) {
      const networkError = new Error(
        error.message || 'Network error. Please check your connection and try again.'
      );
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Reject a unit request (landlord / agent on assigned property)
 * Endpoint: POST /api/properties/{propertyId}/unit-requests/{requestId}/reject
 * @param {string} propertyId
 * @param {string} requestId
 * @param {{ reason?: string }} [payload]
 * @returns {Promise<Object>} Response with status, message, and data (updated request)
 */
export const rejectUnitRequest = async (propertyId, requestId, { reason } = {}) => {
  try {
    const url = `${API_BASE_URL}/api/properties/${propertyId}/unit-requests/${requestId}/reject`;
    const body = {};
    if (reason != null && String(reason).trim() !== '') {
      const trimmed = String(reason).trim();
      body.reason = trimmed;
      body.rejectionReason = trimmed;
      body.rejection_reason = trimmed;
      body.cancelReason = trimmed;
    }

    const response = await apiClient(
      url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to reject unit request';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Reject unit request error:', error);
    if (!error.response) {
      const networkError = new Error(
        error.message || 'Network error. Please check your connection and try again.'
      );
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Accept agreement for an approved unit request (tenant)
 * Endpoint: POST /api/unit-requests/{requestId}/accept-agreement
 * @param {string} requestId
 * @returns {Promise<Object>} Response with status, message, and data (updated request)
 */
export const acceptUnitRequestAgreement = async (requestId) => {
  try {
    const url = `${API_BASE_URL}/api/unit-requests/${requestId}/accept-agreement`;
    const response = await apiClient(url, { method: 'POST' }, true);
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to accept agreement';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Accept unit request agreement error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Cancel a unit request (tenant) before it's accepted
 * Endpoint: POST /api/unit-requests/{requestId}/cancel
 * @param {string} requestId
 * @returns {Promise<Object>} Response with status, message, and data (updated request)
 */
export const cancelUnitRequest = async (requestId) => {
  try {
    const url = `${API_BASE_URL}/api/unit-requests/${requestId}/cancel`;
    const response = await apiClient(url, { method: 'POST' }, true);
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to cancel unit request';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Cancel unit request error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get payment details for a unit request (tenant)
 * Endpoint: GET /api/unit-requests/{requestId}/payment-details
 * @param {string} requestId
 * @returns {Promise<Object>} Response with status, message, and data (payment details)
 */
export const getUnitRequestPaymentDetails = async (requestId) => {
  try {
    const url = `${API_BASE_URL}/api/unit-requests/${requestId}/payment-details`;
    const response = await apiClient(url, { method: 'GET' }, true);
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch payment details';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get unit request payment details error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Confirm tenant payments (owner / agent)
 * Endpoint: POST /api/properties/{propertyId}/unit-requests/{requestId}/confirm-payments
 * @param {string} propertyId
 * @param {string} requestId
 * @param {{ depositPaid: boolean, firstMonthPaid: boolean }} payload
 * @returns {Promise<Object>} Response with status, message, and data (updated request)
 */
export const confirmUnitRequestPayments = async (propertyId, requestId, { depositPaid, firstMonthPaid }) => {
  try {
    const url = `${API_BASE_URL}/api/properties/${propertyId}/unit-requests/${requestId}/confirm-payments`;
    const response = await apiClient(
      url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          depositPaid: Boolean(depositPaid),
          firstMonthPaid: Boolean(firstMonthPaid),
        }),
      },
      true
    );
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to confirm payments';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Confirm unit request payments error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Reject deposit / first-month payment evidence on a unit request (owner / agent).
 * Endpoint: POST /api/properties/{propertyId}/unit-requests/{requestId}/reject-payments
 * @param {string} propertyId
 * @param {string} requestId
 * @param {{ reason?: string }} [payload]
 */
export const rejectUnitRequestPayments = async (propertyId, requestId, { reason } = {}) => {
  const id = String(requestId || '').trim();
  const body = JSON.stringify(buildRejectReasonBody(reason));
  const urls = [
    `${API_BASE_URL}/api/properties/${propertyId}/unit-requests/${id}/reject-payments`,
    `${API_BASE_URL}/api/unit-requests/${id}/reject-payments`,
  ];

  let lastError = null;

  for (const url of urls) {
    try {
      const response = await apiClient(
        url,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
        },
        true
      );

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        return data;
      }

      const errorMessage =
        data.message || data.error || 'Failed to reject unit request payment evidence';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      lastError = error;
      if (response.status !== 404) {
        throw error;
      }
    } catch (error) {
      console.error('Reject unit request payments error:', url, error);
      lastError = error;
      if (!error.response) {
        const networkError = new Error(
          error.message || 'Network error. Please check your connection and try again.'
        );
        networkError.isNetworkError = true;
        throw networkError;
      }
      if (error.status !== 404) {
        throw error;
      }
    }
  }

  throw (
    lastError ||
    new Error(
      'Could not reject payment evidence. The server may not expose reject-payments yet — use Rent evidence or contact support.'
    )
  );
};

/**
 * Assign a tenant to a unit (owner / agent)
 * Endpoint: POST /api/properties/{propertyId}/units/{unitId}/assign-tenant
 * @param {string} propertyId
 * @param {string} unitId
 * @param {{ userId: string, startDate?: string, agreementAccepted: true }} payload
 */
export const assignTenantToUnit = async (propertyId, unitId, { userId, startDate, agreementAccepted }) => {
  try {
    const url = `${API_BASE_URL}/api/properties/${propertyId}/units/${unitId}/assign-tenant`;
    const body = {
      userId: String(userId || '').trim(),
      agreementAccepted: Boolean(agreementAccepted),
    };
    if (startDate && String(startDate).trim()) {
      body.startDate = String(startDate).trim();
    }

    const response = await apiClient(
      url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      true
    );
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to assign tenant to unit';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Assign tenant to unit error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Assign a tenant directly to a unit (manual)
 * Endpoint: POST /api/properties/{propertyId}/units/{unitId}/assign-rental-manual
 * @param {string} propertyId
 * @param {string} unitId
 * @param {{ userId: string, startDate?: string, amountPaid?: number, agreementAccepted: boolean }} payload
 */
export const assignRentalManual = async (propertyId, unitId, { userId, startDate, amountPaid, agreementAccepted }) => {
  try {
    const url = `${API_BASE_URL}/api/properties/${propertyId}/units/${unitId}/assign-rental-manual`;
    const body = {
      userId: String(userId || '').trim(),
      agreementAccepted: Boolean(agreementAccepted),
      amountPaid: amountPaid ? Number(amountPaid) : 0,
    };
    if (startDate && String(startDate).trim()) {
      body.startDate = String(startDate).trim();
    }

    const response = await apiClient(
      url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      true
    );
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to assign rental manually';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Assign rental manual error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Assign a tenant directly to a BnB unit (manual)
 * Endpoint: POST /api/properties/{propertyId}/units/{unitId}/assign-bnb-manual
 * @param {string} propertyId
 * @param {string} unitId
 * @param {{ userId: string, startDate: string, endDate: string, amountPaid: number, agreementAccepted: boolean }} payload
 */
export const assignBnbManual = async (propertyId, unitId, { userId, startDate, endDate, amountPaid, agreementAccepted }) => {
  try {
    const url = `${API_BASE_URL}/api/properties/${propertyId}/units/${unitId}/assign-bnb-manual`;
    const body = {
      userId: String(userId || '').trim(),
      startDate: String(startDate || '').trim(),
      endDate: String(endDate || '').trim(),
      amountPaid: amountPaid ? Number(amountPaid) : 0,
      agreementAccepted: Boolean(agreementAccepted),
    };

    const response = await apiClient(
      url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
      true
    );
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to assign BnB manually';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Assign BnB manual error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};


/**
 * Submit deposit + first month payment evidence (tenant)
 * Endpoint: POST /api/unit-requests/{requestId}/submit-payments
 * @param {string} requestId
 * @param {{
 *  deposit: { amountPaid?: number, mpesaMessage?: string, photoUrl?: string },
 *  firstMonth: { amountPaid?: number, mpesaMessage?: string, photoUrl?: string }
 * }} payload
 * @returns {Promise<Object>} Response with status, message, and data (updated request)
 */
export const submitUnitRequestPaymentsEvidence = async (requestId, payload) => {
  try {
    const url = `${API_BASE_URL}/api/unit-requests/${requestId}/submit-payments`;
    const response = await apiClient(
      url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
      true
    );
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to submit payment evidence';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Submit unit request payments evidence error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Submit payment evidence for a BnB booking
 * Endpoint: POST /api/bnb/bookings/{bookingId}/payment-evidence
 * @param {string} bookingId
 * @param {{ amount?: number, mpesaMessage?: string, photoUrl?: string }} payload
 * @returns {Promise<Object>} Response with status, message, and data
 */
export const submitBnbBookingPaymentEvidence = async (bookingId, payload) => {
  try {
    const url = `${API_BASE_URL}/api/bnb/bookings/${bookingId}/payment-evidence`;
    const response = await apiClient(
      url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
      true
    );
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to submit BnB payment evidence';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Submit BnB payment evidence error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Upload payment evidence photo to /api/files/upload.
 * Note: backend category isn't specified for payment evidence; using 'payment' by default.
 * @param {File} file
 * @returns {Promise<Object>} Upload response with status and data containing URL
 */
export const uploadPaymentEvidencePhoto = async (file) => {
  try {
    const token = getAuthToken();

    if (!token) {
      const error = new Error('Authentication token not found. Please log in again.');
      error.status = 401;
      throw error;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', 'payment');

    const response = await apiClient(
      `${API_BASE_URL}/api/files/upload`,
      {
        method: 'POST',
        body: formData,
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'File upload failed';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Upload payment evidence photo error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Update an existing property
 * @param {string} propertyId - Property ID
 * @param {Object} propertyData - Property update data (all fields optional)
 * @param {string} propertyData.name - Property name
 * @param {string} propertyData.address - Property address
 * @param {Object} propertyData.location - Location object with lat, lng, city (and optional area/locality/suburb)
 * @param {string} propertyData.description - Property description
 * @param {Array<string>} propertyData.amenities - Array of amenities
 * @param {Array<string>} propertyData.imageUrls - Array of image URLs
 * @param {Object} propertyData.contacts - Contacts object with phone, email, caretakerPhone
 * @param {Object} propertyData.paymentDetails - Payment details object
 * @param {string} propertyData.agreementUrl - Agreement document URL (optional)
 * @param {number} propertyData.unitCount - Number of units
 * @param {boolean} propertyData.published - Whether property is publicly visible (optional)
 * @param {number} propertyData.rentGraceDays - Rent grace period in days (optional)
 * @param {string} propertyData.lateFeeType - FIXED_PER_DAY | PERCENT_OF_RENT_PER_DAY (optional)
 * @param {number} propertyData.lateFeeValue - Late fee value (optional)
 * @param {boolean} propertyData.waterIncludedInRent - Whether water is included in rent (optional)
 * @param {number} propertyData.waterRatePerUnit - Water charge per unit when not included in rent (optional)
 * @returns {Promise<Object>} Update property response
 */
export const updateProperty = async (propertyId, propertyData) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/properties/${propertyId}`,
      {
        method: 'PUT',
        body: JSON.stringify(propertyData),
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to update property';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Update property error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Delete a property
 * Endpoint: DELETE /api/properties/{propertyId}
 * @param {string} propertyId - Property ID
 * @returns {Promise<Object>} Delete property response
 */
export const deleteProperty = async (propertyId) => {
  try {
    const propertyIdValue = String(propertyId || '').trim();
    if (!propertyIdValue) {
      throw new Error('Property ID is required');
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/properties/${encodeURIComponent(propertyIdValue)}`,
      {
        method: 'DELETE',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to delete property';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Delete property error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Assign an agent to manage a property (invite)
 * Endpoint: POST /api/properties/{propertyId}/agents
 * @param {string} propertyId
 * @param {Object} payload
 * @param {string} payload.agentId - Agent UUID
 * @param {string} payload.role - MANAGER | VIEWER
 * @param {string} payload.notes - Optional
 * @returns {Promise<Object>}
 */
export const assignAgentToProperty = async (propertyId, payload) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/properties/${propertyId}/agents`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to assign agent to property';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Assign agent to property error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get agents assigned/invited to a property.
 * Endpoint: GET /api/properties/{propertyId}/agents
 * @param {string} propertyId
 * @returns {Promise<Object>}
 */
export const getPropertyAgents = async (propertyId) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/properties/${propertyId}/agents`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch property agents';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get property agents error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Update an existing property-agent assignment role.
 * Endpoint: PATCH /api/properties/{propertyId}/agents/{assignmentId}/role
 * @param {string} propertyId
 * @param {string} assignmentId
 * @param {{ role: 'MANAGER' | 'VIEWER' }} payload
 * @returns {Promise<Object>}
 */
export const updatePropertyAgentRole = async (propertyId, assignmentId, payload) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/properties/${propertyId}/agents/${assignmentId}/role`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to update agent role';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Update property agent role error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Upload unit image
 * @param {File} file - Image file to upload
 * @param {string} unitId - Unit ID (optional, for existing units)
 * @returns {Promise<Object>} Upload response with status and data containing URL
 */
export const uploadUnitImage = async (file, unitId = null) => {
  try {
    const token = getAuthToken();

    if (!token) {
      const error = new Error('Authentication token not found. Please log in again.');
      error.status = 401;
      throw error;
    }

    const formData = new FormData();
    formData.append('file', file);
    if (unitId) {
      formData.append('unitId', unitId);
    }
    formData.append('category', 'unit');

    const response = await apiClient(
      `${API_BASE_URL}/api/files/upload`,
      {
        method: 'POST',
        body: formData,
      },
      true // Requires authentication
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'File upload failed';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Upload unit image error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Create a new unit for a property
 * @param {string} propertyId - Property ID
 * @param {Object} unitData - Unit data
 * @param {string} unitData.unitNumber - Unit number (e.g., "A1")
 * @param {string} unitData.name - Unit name (e.g., "Unit A1 - Single Room")
 * @param {string} unitData.description - Unit description
 * @param {string} unitData.unitTypeDefinitionId - Unit type ID
 * @param {string} unitData.unitCategoryDefinitionId - Unit category ID
 * @param {string} unitData.unitSubCategoryId - Unit subcategory ID
 * @param {number} unitData.bedrooms - Number of bedrooms
 * @param {number} unitData.bathrooms - Number of bathrooms
 * @param {number} unitData.sizeSqm - Size in square meters
 * @param {Array<string>} unitData.amenities - Array of amenities
 * @param {Array<string>} unitData.photos - Array of photo URLs (2-6 URLs)
 * @param {number} unitData.monthlyRent - Monthly rent (required for rental)
 * @param {number} unitData.depositAmount - Deposit amount (required for rental)
 * @param {number} unitData.rentDueDay - Rent due day (optional, 1-28 recommended)
 * @param {number} unitData.pricePerNight - Price per night (required for BNB)
 * @param {string} unitData.checkoutTime - Checkout time (required for BNB, e.g., "11:00")
 * @param {boolean} unitData.isPublic - Whether unit is visible publicly (optional)
 * @returns {Promise<Object>} Create unit response
 */
export const createUnit = async (propertyId, unitData) => {
  try {
    // Add logging to verify the propertyId being used
    const url = `${API_BASE_URL}/api/properties/${propertyId}/units`;
    console.log('createUnit API call - propertyId:', propertyId);
    console.log('createUnit API call - URL:', url);
    console.log('createUnit API call - unitData:', unitData);

    const response = await apiClient(
      url,
      {
        method: 'POST',
        body: JSON.stringify(unitData),
      },
      true // Requires authentication
    );

    const data = await response.json();
    console.log('createUnit API response:', data);

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to create unit';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Create unit error:', error);
    console.error('Create unit error - propertyId was:', propertyId);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Update an existing unit
 * @param {string} propertyId - Property ID
 * @param {string} unitId - Unit ID
 * @param {Object} unitData - Unit update data (all fields optional)
 * @param {string} unitData.unitNumber - Unit number
 * @param {string} unitData.name - Unit name
 * @param {string} unitData.description - Unit description
 * @param {string} unitData.unitTypeDefinitionId - Unit type ID
 * @param {string} unitData.unitCategoryDefinitionId - Unit category ID
 * @param {string} unitData.unitSubCategoryId - Unit subcategory ID
 * @param {number} unitData.bedrooms - Number of bedrooms
 * @param {number} unitData.bathrooms - Number of bathrooms
 * @param {number} unitData.sizeSqm - Size in square meters
 * @param {Array<string>} unitData.amenities - Array of amenities
 * @param {Array<string>} unitData.photos - Array of photo URLs
 * @param {number} unitData.monthlyRent - Monthly rent (for rental)
 * @param {number} unitData.depositAmount - Deposit amount (for rental)
 * @param {number} unitData.rentDueDay - Rent due day (optional, 1-28 recommended)
 * @param {number} unitData.pricePerNight - Price per night (for BNB)
 * @param {string} unitData.checkoutTime - Checkout time (for BNB)
 * @param {boolean} unitData.isPublic - Whether unit is visible publicly (optional)
 * @returns {Promise<Object>} Update unit response
 */
export const updateUnit = async (propertyId, unitId, unitData) => {
  try {
    // Add logging to verify the propertyId being used
    const url = `${API_BASE_URL}/api/properties/${propertyId}/units/${unitId}`;
    console.log('updateUnit API call - propertyId:', propertyId);
    console.log('updateUnit API call - unitId:', unitId);
    console.log('updateUnit API call - URL:', url);
    console.log('updateUnit API call - unitData:', unitData);

    const response = await apiClient(
      url,
      {
        method: 'PUT',
        body: JSON.stringify(unitData),
      },
      true // Requires authentication
    );

    const data = await response.json();
    console.log('updateUnit API response:', data);

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to update unit';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Update unit error:', error);
    console.error('Update unit error - propertyId was:', propertyId);
    console.error('Update unit error - unitId was:', unitId);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Delete a unit from a property
 * Endpoint: DELETE /api/properties/{propertyId}/units/{unitId}
 * @param {string} propertyId - Property ID
 * @param {string} unitId - Unit ID
 * @returns {Promise<Object>} Delete unit response
 */
export const deleteUnit = async (propertyId, unitId) => {
  try {
    const propertyIdValue = String(propertyId || '').trim();
    const unitIdValue = String(unitId || '').trim();
    if (!propertyIdValue) {
      throw new Error('Property ID is required');
    }
    if (!unitIdValue) {
      throw new Error('Unit ID is required');
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/properties/${encodeURIComponent(propertyIdValue)}/units/${encodeURIComponent(unitIdValue)}`,
      {
        method: 'DELETE',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to delete unit';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Delete unit error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get units for a property
 * @param {string} propertyId - Property ID
 * @returns {Promise<Object>} Units response with array of units
 */
export const getPropertyUnits = async (propertyId, options = {}) => {
  try {
    const params = new URLSearchParams();
    const scope = String(options?.dashboardScope || '').trim();
    if (scope) params.set('dashboardScope', scope);
    const qs = params.toString();
    const url = `${API_BASE_URL}/api/properties/${propertyId}/units${qs ? `?${qs}` : ''}`;
    console.log('Fetching units from URL:', url);
    console.log('PropertyId:', propertyId);

    const response = await apiClient(
      url,
      {
        method: 'GET',
      },
      true // Requires authentication
    );

    const data = await response.json();
    console.log('Raw units API response:', data);
    console.log('Response status:', response.status);
    console.log('Response ok:', response.ok);

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch property units';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get property units error:', error);
    console.error('Error status:', error.status);
    console.error('Error response:', error.response);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get tenancy history for a property (landlord / agent).
 * Endpoint: GET /api/properties/{propertyId}/tenancy-history
 * @param {string} propertyId - Property ID
 * @returns {Promise<Object>} Tenancy rows for the property
 */
export const getPropertyTenancyHistory = async (propertyId) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/properties/${propertyId}/tenancy-history`,
      {
        method: 'GET',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch property tenancy history';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get property tenancy history error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get tenancy history for a specific unit in a property.
 * Endpoint: GET /api/properties/{propertyId}/units/{unitId}/tenancy-history
 * @param {string} propertyId - Property ID
 * @param {string} unitId - Unit ID
 * @returns {Promise<Object>} Tenancy rows for the unit
 */
export const getPropertyUnitTenancyHistory = async (propertyId, unitId) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/properties/${propertyId}/units/${unitId}/tenancy-history`,
      {
        method: 'GET',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch unit tenancy history';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    if (!isUnitNotFoundError(error)) {
      console.error('Get property unit tenancy history error:', error);
    }
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get public properties for tenant listings (no auth required)
 * Endpoint: GET /api/public/properties
 * @param {Object} params - Query parameters
 * @param {number} params.page - Page number (default: 0)
 * @param {number} params.size - Page size (default: 20)
 * @param {string} params.search - Free-text search across name/address (optional)
 * @param {string} params.city - City filter (optional)
 * @param {string} params.area - Area / neighbourhood filter (optional)
 * @param {string|string[]} params.amenities - Amenity filter(s) (optional)
 * @returns {Promise<Object>} Properties response with pagination
 */
export const getPublicProperties = async ({
  page = 0,
  size = 20,
  search = '',
  city = '',
  area = '',
  amenities = '',
  minPrice = '',
  maxPrice = '',
  minBedrooms = '',
  minBathrooms = '',
  forSale = false,
} = {}) => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (search && search.trim()) {
      queryParams.append('search', search.trim());
    }

    if (city && city.trim()) {
      queryParams.append('city', city.trim());
    }

    if (area && area.trim()) {
      queryParams.append('area', area.trim());
    }

    if (amenities && Array.isArray(amenities)) {
      amenities.forEach((amenity) => {
        if (amenity && amenity.trim()) {
          queryParams.append('amenities', amenity.trim());
        }
      });
    } else if (amenities && typeof amenities === 'string' && amenities.trim()) {
      queryParams.append('amenities', amenities.trim());
    }

    if (minPrice !== '' && minPrice != null) queryParams.append('minPrice', String(minPrice));
    if (maxPrice !== '' && maxPrice != null) queryParams.append('maxPrice', String(maxPrice));
    if (minBedrooms !== '' && minBedrooms != null) queryParams.append('minBedrooms', String(minBedrooms));
    if (minBathrooms !== '' && minBathrooms != null) queryParams.append('minBathrooms', String(minBathrooms));
    if (forSale) queryParams.append('forSale', 'true');

    const response = await apiClient(
      `${API_BASE_URL}/api/public/properties?${queryParams.toString()}`,
      {
        method: 'GET',
      },
      false // Public endpoint – no authentication required
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch public properties';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get public properties error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Normalizes GET /api/public/bnb/units payload to a unit array.
 * Backend may return `data` as an array or as `{ content: [...] }` (paginated).
 */
export const normalizePublicBnbUnitsList = (response) => {
  const payload = response?.data ?? response;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  return [];
};

/**
 * Get public BnB units for requested dates (no auth required)
 * Endpoint: GET /api/public/bnb/units
 * @param {Object} params - Query parameters
 * @param {string} params.checkIn - Check-in date (YYYY-MM-DD)
 * @param {string} params.checkOut - Check-out date (YYYY-MM-DD)
 * @returns {Promise<Object>} Public BnB units response
 */
export const getPublicBnbUnits = async ({
  checkIn = '',
  checkOut = '',
  search = '',
  minPrice = '',
  maxPrice = '',
} = {}) => {
  try {
    const queryParams = new URLSearchParams();
    if (checkIn && String(checkIn).trim()) queryParams.append('checkIn', String(checkIn).trim());
    if (checkOut && String(checkOut).trim()) queryParams.append('checkOut', String(checkOut).trim());
    if (search && String(search).trim()) queryParams.append('search', String(search).trim());
    if (minPrice !== '' && minPrice != null) queryParams.append('minPrice', String(minPrice));
    if (maxPrice !== '' && maxPrice != null) queryParams.append('maxPrice', String(maxPrice));

    const response = await apiClient(
      `${API_BASE_URL}/api/public/bnb/units?${queryParams.toString()}`,
      {
        method: 'GET',
      },
      false // Public endpoint – no authentication required
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch public BnB units';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return {
      ...data,
      data: normalizePublicBnbUnitsList(data),
    };
  } catch (error) {
    console.error('Get public BnB units error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Cancel a BnB booking (authenticated)
 * Endpoint: POST /api/bnb/bookings/{bookingId}/cancel
 * @param {string} bookingId - Booking ID
 * @param {{ reason?: string }} [body]
 * @returns {Promise<Object>} Cancel response with updated booking in data
 */
export const cancelBnbBooking = async (bookingId, { reason } = {}) => {
  try {
    const id = String(bookingId || '').trim();
    if (!id) throw new Error('Booking id is required');

    const payload = {};
    if (reason != null && String(reason).trim() !== '') {
      const trimmed = String(reason).trim();
      payload.reason = trimmed;
      payload.rejectionReason = trimmed;
      payload.rejection_reason = trimmed;
      payload.cancelReason = trimmed;
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/bnb/bookings/${id}/cancel`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to cancel BnB booking';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Cancel BnB booking error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Reject a BnB booking (owner / agent)
 * Endpoint: POST /api/bnb/bookings/{bookingId}/reject
 * @param {string} bookingId - Booking ID
 * @param {{ reason?: string }} [body]
 * @returns {Promise<Object>} Reject response with updated booking in data
 */
export const rejectBnbBooking = async (bookingId, { reason } = {}) => {
  try {
    const id = String(bookingId || '').trim();
    if (!id) throw new Error('Booking id is required');

    const payload = {};
    if (reason != null && String(reason).trim() !== '') {
      const trimmed = String(reason).trim();
      payload.reason = trimmed;
      payload.rejectionReason = trimmed;
      payload.rejection_reason = trimmed;
      payload.cancelReason = trimmed;
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/bnb/bookings/${id}/reject`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to reject BnB booking';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Reject BnB booking error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Normalizes GET /api/bnb/units/{unitId}/blocked-dates payload to an array.
 * @param {Object} response
 * @returns {Array<Object>}
 */
export const normalizeBnbBlockedDatesList = (response) => {
  const d = response?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.content)) return d.content;
  if (Array.isArray(d?.blockedDates)) return d.blockedDates;
  if (Array.isArray(response?.blockedDates)) return response.blockedDates;
  return [];
};

/**
 * Get blocked date ranges for a BnB unit.
 * Endpoint: GET /api/bnb/units/{unitId}/blocked-dates
 * @param {string} unitId - Unit ID
 * @returns {Promise<Object>} API response
 */
export const getBnbUnitBlockedDates = async (unitId) => {
  try {
    const id = String(unitId || '').trim();
    if (!id) throw new Error('Unit id is required');

    const response = await apiClient(
      `${API_BASE_URL}/api/bnb/units/${encodeURIComponent(id)}/blocked-dates`,
      { method: 'GET' },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to load blocked dates';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get BnB unit blocked dates error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Block dates on a BnB unit so tenants cannot book that range.
 * Endpoint: POST /api/bnb/units/{unitId}/block-dates
 * @param {string} unitId - Unit ID
 * @param {{ startDate: string, endDate: string, reason?: string }} body
 * @returns {Promise<Object>} API response
 */
export const blockBnbUnitDates = async (unitId, { startDate, endDate, reason } = {}) => {
  try {
    const id = String(unitId || '').trim();
    if (!id) throw new Error('Unit id is required');

    const start = String(startDate || '').trim();
    const end = String(endDate || '').trim();
    if (!start) throw new Error('Start date is required');
    if (!end) throw new Error('End date is required');

    const payload = { startDate: start, endDate: end };
    if (reason != null && String(reason).trim() !== '') {
      payload.reason = String(reason).trim();
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/bnb/units/${encodeURIComponent(id)}/block-dates`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to block unit dates';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Block BnB unit dates error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Remove a blocked date range (unblock).
 * Endpoint: DELETE /api/bnb/blocked-dates/{blockedDateId}
 * @param {string} blockedDateId - Blocked date record ID
 * @returns {Promise<Object>} API response
 */
export const unblockBnbBlockedDate = async (blockedDateId) => {
  try {
    const id = String(blockedDateId || '').trim();
    if (!id) throw new Error('Blocked date id is required');

    const response = await apiClient(
      `${API_BASE_URL}/api/bnb/blocked-dates/${encodeURIComponent(id)}`,
      { method: 'DELETE' },
      true
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to unblock dates';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Unblock BnB blocked date error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get public BnB unit details for requested dates (no auth required)
 * Endpoint: GET /api/public/bnb/units/{unitId}
 * @param {string} unitId - Unit ID
 * @param {Object} params - Query parameters
 * @param {string} params.checkIn - Check-in date (YYYY-MM-DD)
 * @param {string} params.checkOut - Check-out date (YYYY-MM-DD)
 * @returns {Promise<Object>} Public BnB unit response
 */
export const getPublicBnbUnitById = async (unitId, { checkIn = '', checkOut = '' } = {}) => {
  try {
    const id = String(unitId || '').trim();
    if (!id) throw new Error('Unit id is required');

    const queryParams = new URLSearchParams();
    if (checkIn && String(checkIn).trim()) {
      const val = String(checkIn).trim();
      queryParams.append('checkIn', val);
      queryParams.append('checkInDate', val);
    }
    if (checkOut && String(checkOut).trim()) {
      const val = String(checkOut).trim();
      queryParams.append('checkOut', val);
      queryParams.append('checkOutDate', val);
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/public/bnb/units/${id}?${queryParams.toString()}`,
      {
        method: 'GET',
      },
      false
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch public BnB unit';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get public BnB unit by ID error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Create a BnB booking (authenticated)
 * Endpoint: POST /api/bnb/bookings
 * @param {Object} payload - Booking payload
 * @param {string} payload.unitId - Unit ID
 * @param {string} payload.checkInDate - Check-in date (YYYY-MM-DD)
 * @param {string} payload.checkOutDate - Check-out date (YYYY-MM-DD)
 * @param {string} [payload.notes] - Optional notes
 * @returns {Promise<Object>} Booking response
 */
export const createBnbBooking = async (payload) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/bnb/bookings`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to create BnB booking';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Create BnB booking error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Create multiple units for a property using a template
 * @param {string} propertyId - Property ID
 * @param {Object} bulkData - Bulk unit creation data
 * @param {Object} bulkData.template - Unit template (same fields as createUnit except unitNumber)
 * @param {string} bulkData.template.name - Unit name template (e.g., "1BR Unit")
 * @param {string} bulkData.template.description - Unit description
 * @param {string} bulkData.template.unitTypeDefinitionId - Unit type ID
 * @param {string} bulkData.template.unitCategoryDefinitionId - Unit category ID
 * @param {string} bulkData.template.unitSubCategoryId - Unit subcategory ID
 * @param {number} bulkData.template.bedrooms - Number of bedrooms
 * @param {number} bulkData.template.bathrooms - Number of bathrooms
 * @param {number} bulkData.template.sizeSqm - Size in square meters
 * @param {Array<string>} bulkData.template.amenities - Array of amenities
 * @param {Array<string>} bulkData.template.photos - Array of photo URLs (2-6 URLs)
 * @param {number} bulkData.template.monthlyRent - Monthly rent (required for rental)
 * @param {number} bulkData.template.depositAmount - Deposit amount (required for rental)
 * @param {number} bulkData.template.rentDueDay - Rent due day (optional, 1-28 recommended)
 * @param {number} bulkData.template.pricePerNight - Price per night (required for BNB)
 * @param {string} bulkData.template.checkoutTime - Checkout time (required for BNB)
 * @param {boolean} bulkData.template.isPublic - Whether unit is visible publicly (optional)
 * @param {Array<string>} bulkData.unitNumbers - Array of unit numbers (1-100 units)
 * @returns {Promise<Object>} Bulk create units response
 */
export const createBulkUnits = async (propertyId, bulkData) => {
  try {
    // Add logging to verify the propertyId being used
    const url = `${API_BASE_URL}/api/properties/${propertyId}/units/bulk`;
    console.log('createBulkUnits API call - propertyId:', propertyId);
    console.log('createBulkUnits API call - URL:', url);
    console.log('createBulkUnits API call - bulkData:', bulkData);

    const response = await apiClient(
      url,
      {
        method: 'POST',
        body: JSON.stringify(bulkData),
      },
      true // Requires authentication
    );

    const data = await response.json();
    console.log('createBulkUnits API response:', data);

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to create bulk units';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Create bulk units error:', error);
    console.error('Create bulk units error - propertyId was:', propertyId);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error. Please check your connection and try again.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Get rent status for a property.
 * GET /api/billing/properties/{propertyId}/rent-status?year=&month=
 * @param {string} propertyId
 * @param {number} year
 * @param {number} month
 */
export const getRentStatus = async (propertyId, year, month) => {
  try {
    const params = new URLSearchParams({
      year: String(year),
      month: String(month),
    });

    const url = `${API_BASE_URL}/api/billing/properties/${propertyId}/rent-status?${params.toString()}`;
    const response = await apiClient(url, { method: 'GET' }, true);
    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data.message || data.error || 'Failed to fetch rent status';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Get rent status error:', error);
    if (!error.response) {
      const networkError = new Error(error.message || 'Network error.');
      networkError.isNetworkError = true;
      throw networkError;
    }
    throw error;
  }
};

/**
 * Submit a rating for a property.
 * Endpoint: POST /api/properties/{propertyId}/rating
 * @param {string} propertyId
 * @param {{ score: number, message: string }} ratingData
 * @returns {Promise<Object>} Rating response
 */
export const postPropertyRating = async (propertyId, ratingData) => {
  try {
    const response = await apiClient(
      `${API_BASE_URL}/api/properties/${propertyId}/rating`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ratingData),
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to submit property rating';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('postPropertyRating error:', error);
    if (!error.response) {
      throw new Error('Network error. Please try again.');
    }
    throw error;
  }
};

/**
 * Get ratings for a property (landlord / agent).
 * Endpoint: GET /api/properties/{propertyId}/rating/my?page=&size=
 * @param {string} propertyId
 * @param {Object} params - Pagination parameters
 * @param {number} params.page - Page index
 * @param {number} params.size - Page size
 * @returns {Promise<Object>} Ratings response
 */
export const getPropertyRatings = async (propertyId, { page = 0, size = 20, dashboardScope } = {}) => {
  try {
    const queryParams = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (dashboardScope && String(dashboardScope).trim() !== '') {
      queryParams.append('dashboardScope', String(dashboardScope).trim());
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/properties/${propertyId}/rating/my?${queryParams.toString()}`,
      {
        method: 'GET',
      },
      true
    );

    const data = await response.json();

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch property ratings';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getPropertyRatings error:', error);
    if (!error.response) {
      throw new Error('Network error. Please try again.');
    }
    throw error;
  }
};

/**
 * Get public ratings and comments for a property.
 * Endpoint: GET /api/properties/{propertyId}/ratings
 * @param {string} propertyId
 * @returns {Promise<Object>} Ratings summary + entries
 */
export const getPropertyRatingsPublic = async (propertyId) => {
  try {
    const id = String(propertyId || '').trim();
    if (!id) {
      throw new Error('Property ID is required');
    }

    const response = await apiClient(
      `${API_BASE_URL}/api/properties/${encodeURIComponent(id)}/ratings`,
      {
        method: 'GET',
      },
      false
    );

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to fetch property ratings';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('getPropertyRatingsPublic error:', error);
    if (!error.response) {
      throw new Error('Network error. Please try again.');
    }
    throw error;
  }
};
/**
 * Generates a shareable link for a property unit.
 * Endpoint: GET /api/public/share/properties/{propertyId}/units/{unitId}
 * @param {string} propertyId
 * @param {string} unitId
 * @returns {Promise<Object>} Share metadata including shareUrl.
 */
export const generateUnitShareLink = async (propertyId, unitId) => {
  try {
    const url = `${API_BASE_URL}/api/public/share/properties/${propertyId}/units/${unitId}`;
    const response = await apiClient(url, { method: 'GET' }, false);
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to generate share link';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('generateUnitShareLink error:', error);
    if (!error.response) {
      throw new Error('Network error. Please try again.');
    }
    throw error;
  }
};

/**
 * Generates a shareable link for a property.
 * Endpoint: GET /api/public/share/properties/{propertyId}
 * @param {string} propertyId
 * @returns {Promise<Object>} Share metadata including shareUrl.
 */
export const generatePropertyShareLink = async (propertyId) => {
  try {
    const url = `${API_BASE_URL}/api/public/share/properties/${propertyId}`;
    const response = await apiClient(url, { method: 'GET' }, false);
    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const errorMessage = data?.message || data?.error || 'Failed to generate share link';
      const error = new Error(errorMessage);
      error.response = data;
      error.status = response.status;
      throw error;
    }

    return data;
  } catch (error) {
    console.error('generatePropertyShareLink error:', error);
    if (!error.response) {
      throw new Error('Network error. Please try again.');
    }
    throw error;
  }
};
